// Auth client for the web portal.
//
// All real authentication happens on the app server's Better Auth instance
// (/api/auth/*), which sets a session cookie on `.pawsfree.net` that this portal
// (web/admin.pawsfree.net) shares cross-subdomain. We call it directly with
// credentials so the cookie is set on the parent domain.
//
// The app-server origin is injected at serve time as window.__APP_SERVER__ (from
// the webserver's PAWSFREE_APP_SERVER_URL), so it's correct per environment
// (staging → app2, prod → app) from a single build. Falls back to a build-time
// VITE_APP_SERVER_URL, then a derived app.<portal-domain> / localhost:3003 (dev).

const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};

declare global {
  interface Window { __APP_SERVER__?: string }
}

function resolveAppServer(): string {
  // Source of truth in production: injected by the webserver from
  // PAWSFREE_APP_SERVER_URL (see webserver/src/app.ts SPA fallback).
  const injected = typeof window !== 'undefined' ? window.__APP_SERVER__ : undefined;
  if (injected) return injected.replace(/\/$/, '');
  const explicit = env.VITE_APP_SERVER_URL;
  if (explicit) return explicit.replace(/\/$/, '');
  const h = window.location.hostname;
  if (h === 'localhost' || h === '127.0.0.1') return 'http://localhost:3003';
  // Portal host → app host, preserving the env suffix: web.pawsfree.net →
  // app.pawsfree.net, web2.pawsfree.net → app2.pawsfree.net. (The injected
  // window.__APP_SERVER__ above is the real source of truth; this fallback only
  // runs when the page wasn't served by our webserver, so keep it
  // environment-correct rather than always deriving prod.)
  const labels = h.split('.');
  labels[0] = labels[0].replace(/^(web|admin)/, 'app');
  return `https://${labels.join('.')}`;
}

export const APP_SERVER = resolveAppServer();

async function authPost<T = unknown>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${APP_SERVER}/api/auth${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body ?? {}),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new Error(
      (data.message as string) || (data.error as string) || `Request failed (${res.status})`,
    );
  }
  return data as T;
}

export const authClient = {
  signInEmail: (email: string, password: string) =>
    authPost('/sign-in/email', { email, password }),
  signUpEmail: (email: string, password: string, name: string, callbackURL?: string) =>
    authPost('/sign-up/email', { email, password, name, callbackURL }),
  /**
   * Returns { url } to redirect the browser to; navigates on success.
   *
   * Social providers cannot mint accounts on their own (`disableImplicitSignUp`
   * on the server) — signing in with an address that has none fails and comes
   * back to `errorCallbackURL` as `?error=signup_disabled`. Only the /welcome
   * signup flow passes `requestSignUp`, which is what re-enables creation for
   * that one surface.
   */
  signInSocial: async (
    provider: 'google' | 'apple',
    callbackURL: string,
    opts?: { requestSignUp?: boolean; errorCallbackURL?: string },
  ) => {
    const { url } = await authPost<{ url?: string }>('/sign-in/social', {
      provider,
      callbackURL,
      ...(opts?.requestSignUp ? { requestSignUp: true } : {}),
      ...(opts?.errorCallbackURL ? { errorCallbackURL: opts.errorCallbackURL } : {}),
    });
    if (url) window.location.href = url;
    else throw new Error('Could not start sign-in.');
  },
  requestPasswordReset: (email: string, redirectTo: string) =>
    authPost('/request-password-reset', { email, redirectTo }),
  resetPassword: (newPassword: string, token: string) =>
    authPost('/reset-password', { newPassword, token }),
  signOut: () => authPost('/sign-out', {}),
};

/** Sign out, then return to the portal (which renders Login when unauthenticated). */
export async function logout(): Promise<void> {
  try {
    await authClient.signOut();
  } catch {
    /* clear locally regardless */
  }
  window.location.assign('/portal');
}
